import { useState, useEffect } from "react";
import SalesMetrics from "../../components/sales/SalesMetrics";
import PageMeta from "../../components/common/PageMeta";
import DateRangePicker from "../../components/daterange/DateRangePicker";
import SalesTable from "../../components/sales/SalesTable";
import MultiSelectCheckbox, { Option as MultiOption } from "../../components/form/MultiSelectCheckbox";
import GroupedSelect, { Product, SelectOption } from "../../components/form/GroupedSelect";
// Importação do Button removida pois agora usamos apenas o DropdownButton
import DropdownButton from "../../components/ui/button/DropdownButton";
import { FileDown } from "lucide-react";
import { exportToCSV, exportToXLS, formatSalesForExport } from "../../utils/exportUtils";
import api from "../../services/api";

// Interface para o tipo de dados da API
interface SalesData {
    summary: {
        totalSales: number;
        netRevenue: number;
        grossRevenue: number;
        paidOrders: number;
    };
    salesList: Array<any>;
    pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
    };
}

export default function MySales() {
    // Estados para pesquisa e filtro
    const [searchTermProdutos, setSearchTermProdutos] = useState("");
    const [paymentMethodFilters, setPaymentMethodFilters] = useState<string[]>([]);
    const [productOfferFilter, setProductOfferFilter] = useState<SelectOption | null>(null);

    // Estados para produtos e ofertas
    const [products, setProducts] = useState<Product[]>([]);

    // Estados para os dados da API
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [salesData, setSalesData] = useState<SalesData | null>(null);

    // Estados para paginação (controlada pelo backend)
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(20);

    // Estado para o termo de busca com debounce (enviado para API)
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

    // Carregar datas do localStorage (se existirem) ou definir padrão
    const getSavedDateRange = () => {
        try {
            const savedRange = localStorage.getItem('dateRangeSelection');
            if (savedRange) {
                const { startDate, endDate } = JSON.parse(savedRange);
                return {
                    startDate: startDate ? new Date(startDate) : null,
                    endDate: endDate ? new Date(endDate) : null
                };
            }
        } catch (error) {
            console.error("Erro ao recuperar datas do localStorage:", error);
        }

        // Se não houver dados salvos ou houver erro, definir hoje
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return {
            startDate: today,
            endDate: today
        };
    };

    const getSavedStatusFilters = (): string[] => {
        const saved = localStorage.getItem("salesStatusFilters");
        if (!saved) return [];

        const parsed = JSON.parse(saved);

        if (Date.now() > parsed.expiresAt) {
            localStorage.removeItem("salesStatusFilters");
            return [];
        }

        return parsed.value || [];
    };


    // Estado para armazenar as datas selecionadas
    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(getSavedDateRange());
    const [statusFilters, setStatusFilters] = useState<string[]>(getSavedStatusFilters());

    // Opções para o multi-select de status (sem "Todos")
    const statusMultiOptions: MultiOption[] = [
        { id: "approved", label: "Paga" },
        { id: "pending", label: "Pendente" },
        { id: "declined", label: "Recusada" },
        { id: "refunded", label: "Reembolsada" },
        { id: "chargeback", label: "Chargeback" }
    ];

    // Opções para o multi-select de método de pagamento (sem "Todos")
    const paymentMethodMultiOptions: MultiOption[] = [
        { id: "card", label: "Cartão" },
        { id: "pix", label: "PIX" },
        { id: "boleto", label: "Boleto" }
    ];

    // Função para buscar os produtos e suas ofertas
    const fetchProductsAndOffers = async () => {
        try {
            // Chamar a API para buscar produtos com suas ofertas
            const response = await api.get('/api/products/with-offers');

            if (response.data && response.data.status === 'success') {
                setProducts(response.data.data || []);
            } else {
                console.error("Erro ao carregar produtos:", response.data);
                setProducts([]);
            }
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
            setProducts([]);
        }
    };

    // Estado para controlar loading da exportação
    const [isExporting, setIsExporting] = useState<boolean>(false);

    // Função para buscar TODOS os dados para exportação (sem paginação)
    const fetchAllSalesForExport = async (): Promise<any[]> => {
        try {
            let url = '/api/sales';
            const params = new URLSearchParams();

            // Função auxiliar para formatar a data no fuso horário brasileiro
            const formatDateBR = (date: Date, endOfDay = false) => {
                const dateCopy = new Date(date);
                if (endOfDay) {
                    dateCopy.setHours(23, 59, 59, 999);
                } else {
                    dateCopy.setHours(0, 0, 0, 0);
                }
                return dateCopy.toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                }).replace(/(\d+)\/(\d+)\/(\d+).*?(\d+):(\d+):(\d+)/, '$3-$2-$1T$4:$5:$6-03:00');
            };

            // Adicionar filtros de data
            if (dateRange.startDate && dateRange.endDate) {
                params.append('startDate', formatDateBR(dateRange.startDate));
                params.append('endDate', formatDateBR(dateRange.endDate, true));
            }

            // Adicionar filtros de produto e oferta
            if (productOfferFilter) {
                if (productOfferFilter.isChildOption) {
                    params.append('offerId', productOfferFilter.value);
                } else {
                    params.append('productId', productOfferFilter.value);
                }
            }

            // Adicionar filtros de método de pagamento
            if (paymentMethodFilters && paymentMethodFilters.length > 0) {
                paymentMethodFilters.forEach((pm) => params.append('paymentMethod', pm));
            }

            // Adicionar filtros de status
            if (statusFilters && statusFilters.length > 0) {
                statusFilters.forEach((st) => params.append('status', st));
            }

            // Adicionar termo de busca
            if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
                params.append('search', debouncedSearchTerm.trim());
            }

            // SEM PAGINAÇÃO - buscar todos os dados para exportação
            // Usar limit alto para garantir que todos os dados sejam retornados
            params.append('limit', '10000');
            params.append('page', '1');

            if (params.toString()) {
                url = `${url}?${params.toString()}`;
            }

            const response = await api.get(url);

            if (response.data && response.data.status === 'success') {
                return response.data.data.salesList || [];
            }

            return [];
        } catch (error) {
            console.error("Erro ao buscar dados para exportação:", error);
            return [];
        }
    };

    // Função para exportar os dados das vendas em CSV ou XLS
    const handleExport = async (format: string) => {
        try {
            setIsExporting(true);

            // Buscar TODOS os dados para exportação
            const allSalesData = await fetchAllSalesForExport();

            if (allSalesData.length === 0) {
                alert("Não há dados para exportar.");
                return;
            }

            // Formatar os dados para exportação
            const formattedData = formatSalesForExport(allSalesData);

            // Nome do arquivo com data atual
            const dateStr = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
            const fileName = `vendas_${dateStr}`;

            // Exportar no formato escolhido
            if (format === 'csv') {
                exportToCSV(formattedData, fileName);
            } else if (format === 'xlsx') {
                exportToXLS(formattedData, fileName);
            }
        } catch (error) {
            console.error("Erro ao exportar:", error);
            alert("Erro ao exportar os dados. Tente novamente.");
        } finally {
            setIsExporting(false);
        }
    };

    // Função para buscar os dados de vendas com filtros
    const fetchSalesData = async (startDate?: Date | null, endDate?: Date | null) => {
        try {
            setIsLoading(true);

            // Preparar parâmetros de data e filtros adicionais
            let url = '/api/sales';
            const params = new URLSearchParams();

            if (startDate && endDate) {
                // Função auxiliar para formatar a data no fuso horário brasileiro
                const formatDateBR = (date: Date, endOfDay = false) => {
                    // Cria uma cópia da data para não modificar a original
                    const dateCopy = new Date(date);

                    // Se for fim do dia, ajusta para 23:59:59
                    if (endOfDay) {
                        dateCopy.setHours(23, 59, 59, 999);
                    } else {
                        dateCopy.setHours(0, 0, 0, 0);
                    }

                    // Ajustar o fuso horário para GMT-3 (Brasil)
                    // Usar o timezone de Brasília (GMT-3)
                    return dateCopy.toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).replace(/(\d+)\/(\d+)\/(\d+).*?(\d+):(\d+):(\d+)/, '$3-$2-$1T$4:$5:$6-03:00');
                };

                // Data inicial começa às 00:00:00 no fuso horário brasileiro
                params.append('startDate', formatDateBR(startDate));

                // Data final vai até 23:59:59 no fuso horário brasileiro
                params.append('endDate', formatDateBR(endDate, true));
            }

            // Adicionar filtros de produto e oferta
            if (productOfferFilter) {
                if (productOfferFilter.isChildOption) {
                    // Se for uma oferta (opção filha)
                    params.append('offerId', productOfferFilter.value);
                } else {
                    // Se for um produto
                    params.append('productId', productOfferFilter.value);
                }
            }

            // Adicionar filtros de método de pagamento (multi)
            if (paymentMethodFilters && paymentMethodFilters.length > 0) {
                paymentMethodFilters.forEach((pm) => params.append('paymentMethod', pm));
            }

            // Adicionar filtros de status (multi)
            if (statusFilters && statusFilters.length > 0) {
                statusFilters.forEach((st) => params.append('status', st));
            }

            // Adicionar parâmetros de paginação
            params.append('page', String(currentPage));
            params.append('limit', String(itemsPerPage));

            // Adicionar termo de busca se existir
            if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
                params.append('search', debouncedSearchTerm.trim());
            }

            // Adicionar os parâmetros à URL se houver algum
            if (params.toString()) {
                url = `${url}?${params.toString()}`;
            }

            // Chamada para a API
            const response = await api.get(url);

            if (response.data && response.data.status === 'success') {
                setSalesData(response.data.data);
            } else {
                console.error("Erro ao carregar dados de vendas:", response.data);
                // Exibir mensagem de erro
                alert("Erro ao carregar os dados de vendas");
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            // Exibir mensagem de erro
            alert("Erro, por favor faça login novamente");
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito para carregar os produtos quando o componente é montado
    useEffect(() => {
        fetchProductsAndOffers();
    }, []);

    // Debounce para o termo de busca (500ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTermProdutos);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTermProdutos]);

    // Efeito para carregar os dados de vendas quando os filtros mudam
    useEffect(() => {
        // Garantir que temos datas válidas para buscar
        if (dateRange.startDate && dateRange.endDate) {
            fetchSalesData(dateRange.startDate, dateRange.endDate);
        }
    }, [dateRange, productOfferFilter, paymentMethodFilters, statusFilters, currentPage, debouncedSearchTerm]); // Recarregar quando os filtros, página ou busca mudam

    // Resetar para página 1 quando filtros mudam (exceto a própria página)
    useEffect(() => {
        setCurrentPage(1);
    }, [dateRange, productOfferFilter, paymentMethodFilters, statusFilters, debouncedSearchTerm]);

    useEffect(() => {
        const TTL = 10 * 60 * 1000; // 10 minutos

        const payload = {
            value: statusFilters,
            expiresAt: Date.now() + TTL
        };

        localStorage.setItem("salesStatusFilters", JSON.stringify(payload));
    }, [statusFilters]);


    // Função para mudar de página
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Função para quando o período de datas mudar
    const handleDateRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
        // Armazenar o novo intervalo de datas selecionado
        setDateRange({
            startDate: range.startDate,
            endDate: range.endDate
        });

        // Não precisamos chamar fetchSalesData aqui, pois o useEffect acima será acionado
        // quando dateRange mudar e fará a chamada para nós
        console.log("Intervalo de datas atualizado:", range);
    };

    return (
        <>
            <PageMeta
                title="Sellyo - Vendas"
                description="Minhas Vendas"
            />
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12">
                    <div className="flex justify-between items-center">
                        <div className="w-auto">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                                Todas as Vendas
                            </h3>
                        </div>
                        <div className="w-auto">
                            <DateRangePicker
                                onChange={handleDateRangeChange}
                                initialStartDate={dateRange.startDate}
                                initialEndDate={dateRange.endDate}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-span-12 space-y-6">
                    <SalesMetrics
                        summary={salesData?.summary}
                        isLoading={isLoading}
                    />
                    {/* Filtros acima da tabela */}
                    <div className="mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Campo de busca */}
                            <div className="relative flex-1">
                                <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                    <svg
                                        className="fill-gray-500 dark:fill-gray-400"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                            fill=""
                                        />
                                    </svg>
                                </button>
                                <input
                                    type="text"
                                    placeholder="Pesquisar transação..."
                                    value={searchTermProdutos}
                                    onChange={(e) => setSearchTermProdutos(e.target.value)}
                                    className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                />
                            </div>

                            {/* Select para Produto / Oferta */}
                            <div className="w-full md:w-64">
                                <GroupedSelect
                                    products={products}
                                    placeholder="Produto / Oferta"
                                    onChange={(option) => setProductOfferFilter(option)}
                                    className="dark:bg-dark-900 h-11"
                                    value={productOfferFilter}
                                />
                            </div>

                            {/* Multi-select para método de pagamento */}
                            <div className="w-full md:w-64">
                                <MultiSelectCheckbox
                                    options={paymentMethodMultiOptions}
                                    selectedValues={paymentMethodFilters}
                                    onChange={(vals) => setPaymentMethodFilters(vals)}
                                    placeholder="Método de pagamento"
                                    showSelectAll={false}
                                    searchable={false}
                                />
                            </div>

                            {/* Multi-select para status */}
                            <div className="w-full md:w-64">
                                <MultiSelectCheckbox
                                    options={statusMultiOptions}
                                    selectedValues={statusFilters}
                                    onChange={(vals) => setStatusFilters(vals)}
                                    placeholder="Filtrar por status"
                                    showSelectAll={false}
                                    searchable={false}
                                />
                            </div>

                            <div className="w-auto">
                                <DropdownButton
                                    size="sm"
                                    variant="primary"
                                    startIcon={isExporting ? (
                                        <svg className="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <FileDown className="size-5" />
                                    )}
                                    disabled={isExporting}
                                    options={[
                                        {
                                            label: "Exportar como CSV",
                                            value: "csv",
                                            onClick: () => handleExport("csv")
                                        },
                                        {
                                            label: "Exportar como XLS",
                                            value: "xls",
                                            onClick: () => handleExport("xlsx")
                                        }
                                    ]}
                                >
                                    {isExporting ? "Exportando..." : "Exportar"}
                                </DropdownButton>
                            </div>
                        </div>
                    </div>
                    <SalesTable
                        salesList={salesData?.salesList}
                        isLoading={isLoading}
                        currentPage={currentPage}
                        totalPages={salesData?.pagination?.pages || 1}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
        </>
    );
}